import { Ability } from '@casl/ability';
import { getTimeWithOffset } from '@dispatch/shared';
import {
  Actions,
  ITaskMessage,
  ResultStatus,
  Subjects,
  TaskRescheduleRequest,
  TaskType,
} from '@dispatch/shared/types';
import { isRunningInJest } from '@dispatch/testing';
import { dateColumn, textColumn } from '@dispatch/web/ui';
import { Stack } from '@mui/material';
import {
  GridColDef,
  GridRowId,
  GridRowParams,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { addMinutes, formatISO } from 'date-fns';
import React, { useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { useParams } from 'react-router';
import Button from '@mui/material/Button';
import { ResourceContext } from '../ResourcesDetails';
import { isTaskReassignable } from '@dispatch/shared/services';

const tableStyleOverrides = {
  '& .MuiDataGrid-iconSeparator': {
    display: 'none',
  },
  '&.MuiDataGrid-root': {
    outline: 'none !important',
    border: 'none !important',
    backgroundColor: '#ffffff',
  },
  '&.MuiDataGrid-root .MuiDataGrid-cell': {
    outline: 'none !important',
    fontStyle: 'normal',
  },
  '&.MuiDataGrid-root .MuiDataGrid-columnHeaderTitleContainer': {
    padding: 0,
  },
  '.MuiDataGrid-columnHeaderTitle': {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: '600',
    fontSize: '14px',
    lineHeight: '24px',
    letterSpacing: '0.17px',
    color: 'rgba(0, 0, 0, 0.87)',
  },
  '.MuiTablePagination-root .MuiTablePagination-selectLabel': {
    color: 'rgba(0, 0, 0, 0.6) !important',
  },
  '&.MuiDataGrid-root .MuiDataGrid-actionsCell .MuiSvgIcon-root': {
    color: 'rgba(0, 0, 0, 0.87)',
    fontSize: '24px',
  },
  '.MuiDataGrid-overlay': {
    height: 'auto !important',
  },
};

// this function will work as long as we keep the taskType naming convention XXX_YYY
// creates a friendly name in the format for Xxx Yyy
const mapTaskTypeFriendlyName = (taskType: string) => {
  const splitString = taskType.split('_');

  return splitString
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
};

const getGridColumns = (showAppointment: boolean, resourceContext: any) => {
  const columns: GridColDef[] = [
    textColumn({
      field: 'claimNumber',
      headerName: 'Claim Number',
      flex: 2,
    }),
    textColumn({
      field: 'insuredName',
      headerName: 'Name',
      flex: 2,
    }),
    textColumn({
      field: 'lossState',
      headerName: 'State',
    }),
    textColumn({
      field: 'lossCity',
      headerName: 'City',
    }),
    textColumn({
      field: 'taskType',
      headerName: 'Task Type',
      valueGetter: (params) =>
        params.value ? mapTaskTypeFriendlyName(params.value) : '',
    }),
  ];

  if (showAppointment) {
    columns.push(
      dateColumn(
        {
          field: 'date',
          headerName: 'Date',
          valueGetter: (params: GridValueGetterParams) =>
            !params.row.appointment
              ? '-'
              : getTimeWithOffset(
                  params.row.timeZoneId,
                  params.row.appointment
                ),
        },
        'MMM dd, yyyy , hh:mm a'
      )
    );
  } else {
    columns.push(
      dateColumn(
        {
          field: 'mustStartAfter',
          headerName: 'Must Start After',
          // flex: 2,
          valueGetter: (params: GridValueGetterParams) =>
            getTimeWithOffset(params.row.timeZoneId, params.row.mustStartAfter),
        },
        'MMM dd, yyyy | HH:mm'
      ),
      dateColumn(
        {
          field: 'mustEndBefore',
          headerName: 'Must End Before',
          // flex: 2,
          valueGetter: (params: GridValueGetterParams) =>
            getTimeWithOffset(params.row.timeZoneId, params.row.mustEndBefore),
        },
        'MMM dd, yyyy | HH:mm'
      ),
      {
        field: 'action',
        headerName: '...',
        sortable: false,
        renderCell: ({ row }: Partial<GridRowParams>) => {
          return (
            isTaskReassignable(row) && (
              <Button
                data-testid="re-assign-button"
                onClick={() => {
                  resourceContext?.resourceMeta?.setTaskToAssign(row);
                }}
              >
                Re-Assign
              </Button>
            )
          );
        },
      }
    );
  }

  return columns;
};
/**
 *
 * Tasks returns a data grid that is specific for the taks tab for the resource view.
 * Dimensions and cofig are a bit different than our global reusable table.
 */
export const Tasks = ({
  taskType,
  height,
  setSelected,
  width = '100%',
  data,
  permissions,
  showAppointment = false,
  resourceFetchStatus,
}: {
  taskType: string;
  height: string;
  width?: string;
  setSelected?: UseFormSetValue<{
    selected: TaskRescheduleRequest[];
    selectedAssignedTasks: ITaskMessage[];
  }>;
  getSelected?: TaskRescheduleRequest[];
  data: { data: ITaskMessage[] | undefined; loading: boolean };
  permissions: Ability<[Actions, Subjects]> | undefined;
  showAppointment?: boolean;
  resourceFetchStatus?: ResultStatus;
}) => {
  const params = useParams();
  const [selectedTasks, setSelectedTasks] = React.useState<GridRowId[]>([]);
  const resourceContext = React.useContext(ResourceContext);

  const pagination = {
    pagingOptions: [5, 10, 15, 20],
    pageSize: 10,
  };

  const [pageSize, setPageSize] = useState(pagination?.pageSize);

  const taskLookup: Record<string, ITaskMessage> | undefined =
    React.useMemo(() => {
      return data?.data?.reduce(function (
        previousValue: Record<string, ITaskMessage>,
        currentValue: ITaskMessage & { id?: string }
      ) {
        previousValue[currentValue.id || ''] = currentValue;
        return previousValue;
      },
      {});
    }, [data?.data]);

  const mapTasks = (ids: GridRowId[]) => {
    return ids.flatMap((id: GridRowId): TaskRescheduleRequest | [] => {
      const task: ITaskMessage | undefined = taskLookup?.[id];

      // if task is not found in lookup, then return empty array for flatMap to clean up
      if (!task || !task.appointment) return [];
      return {
        id: id as string,
        resourceId: params['resourceId'] || '',
        reasonCode: 'other',
        appointmentStart: formatISO(new Date(task.appointment)),
        appointmentEnd: formatISO(
          addMinutes(
            new Date(task?.appointment),
            task?.appointmentDurationInMinutes || 30 // using 30 to prevent 0 duration schedules. This is what is set in scheduler as well
          )
        ),
      };
    });
  };

  const mapData = (ids: GridRowId[]) => {
    setSelectedTasks(ids);
    if (taskType == 'unassigned') {
      setSelected &&
        setSelected('selected', mapTasks(ids), { shouldDirty: true });
    } else {
      setSelected &&
        setSelected(
          'selectedAssignedTasks',
          data.data?.filter((x) => x.id != undefined && ids.includes(x.id)) ||
            [],
          { shouldDirty: true }
        );
    }
  };

  return (
    <div style={{ height, width }}>
      <DataGridPro
        loading={resourceFetchStatus === 'loading'}
        disableVirtualization={isRunningInJest()}
        onSelectionModelChange={mapData}
        rows={data.data || []}
        components={{
          NoRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              No Tasks
            </Stack>
          ),
          NoResultsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              Filter Returned No Tasks
            </Stack>
          ),
        }}
        selectionModel={selectedTasks}
        rowHeight={32}
        columns={[...getGridColumns(showAppointment, resourceContext)]}
        pagination={true}
        pageSize={pageSize}
        rowsPerPageOptions={pagination.pagingOptions}
        onPageSizeChange={(newPage) => {
          setPageSize(newPage);
        }}
        checkboxSelection={permissions?.can?.('upsert', 'resource')}
        sx={{ ...tableStyleOverrides }}
      />
    </div>
  );
};
